import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Clock, Share2, ChevronRight, Loader2 } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import { fetchBlogBySlug, fetchBlogList } from "@/lib/cms";
import { useInternalLinks } from "@/lib/internalLinks";
import RelatedArticles from "@/components/site/RelatedArticles";
import CS01Calculator from "@/components/site/CS01Calculator";

const SITE_BASE = typeof window !== "undefined" ? window.location.origin : "";

function fmt(d) {
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch (e) {
    return d;
  }
}

function toSlug(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined); // undefined=loading, null=not-found
  const [more, setMore] = useState([]);
  const linkify = useInternalLinks();

  useEffect(() => {
    let mounted = true;
    fetchBlogBySlug(slug)
      .then((p) => { if (mounted) setPost(p || null); })
      .catch(() => { if (mounted) setPost(null); });
    fetchBlogList({ pageSize: 50 })
      .then(({ items }) => { if (mounted) setMore(items.filter((x) => x.slug !== slug)); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [slug]);

  const ld = useMemo(() => {
    if (!post) return null;
    return {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.excerpt,
      image: post.cover ? [post.cover] : undefined,
      datePublished: post.publishedAt,
      dateModified: post.updatedAt || post.publishedAt,
      author: { "@type": "Person", name: post.author?.name },
      publisher: {
        "@type": "Organization",
        name: "CoSec Global",
        logo: { "@type": "ImageObject", url: `${SITE_BASE}/favicon.ico` },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_BASE}/blog/${post.slug}` },
      articleSection: post.category,
      keywords: (post.tags || []).join(", "),
    };
  }, [post]);

  if (post === undefined) {
    return (
      <SiteLayout>
        <div className="container-cv py-24 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading article…
        </div>
      </SiteLayout>
    );
  }
  if (post === null) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <SiteLayout
      title={post.title}
      description={post.excerpt}
      image={post.cover}
      type="article"
      jsonLd={ld}
      breadcrumbs={[
        { name: "Home", href: `${SITE_BASE}/` },
        { name: "Blog", href: `${SITE_BASE}/blog` },
        { name: post.category, href: `${SITE_BASE}/blog/category/${toSlug(post.category)}` },
        { name: post.title, href: `${SITE_BASE}/blog/${post.slug}` },
      ]}
    >

      <div className="border-b border-border bg-secondary/40">
        <div className="container-cv py-3 flex items-center gap-1 text-xs text-muted-foreground">
          <Link to="/" className="hover:text-primary">Home</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/blog" className="hover:text-primary">Blog</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to={`/blog/category/${toSlug(post.category)}`} className="hover:text-primary">{post.category}</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-primary font-medium truncate max-w-[60ch]">{post.title}</span>
        </div>
      </div>

      <section className="py-12 md:py-20">
        <div className="container-cv max-w-4xl">
          <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:text-accent transition-colors">
            <ArrowLeft className="h-4 w-4" /> All articles
          </Link>
          <div className="mt-5 flex items-center gap-3 text-xs">
            <Link to={`/blog/category/${toSlug(post.category)}`} className="px-2.5 py-1 rounded-full bg-accent/15 text-primary font-bold uppercase tracking-wider hover:bg-accent/30 transition-colors">{post.category}</Link>
            <span className="text-muted-foreground inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {post.readMins} min read</span>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">{fmt(post.publishedAt)}</span>
          </div>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-primary leading-[1.08] tracking-tight">
            {post.title}
          </h1>
          <p className="mt-5 text-lg md:text-xl text-muted-foreground leading-relaxed">{post.excerpt}</p>

          <div className="mt-7 flex items-center gap-4">
            <div className="h-11 w-11 rounded-full bg-primary text-primary-foreground grid place-items-center font-display font-bold">
              {(post.author?.name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2)}
            </div>
            <div>
              <div className="font-display font-bold text-primary text-sm">{post.author?.name}</div>
              <div className="text-xs text-muted-foreground">{post.author?.role}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-12 md:pb-16">
        <div className="container-cv max-w-5xl">
          <div className="rounded-3xl overflow-hidden border border-border aspect-[16/8]">
            <img src={post.cover} alt={post.title} className="h-full w-full object-cover" />
          </div>
        </div>
      </section>

      <section className="pb-20 md:pb-24">
        <div className="container-cv max-w-3xl space-y-12">
          {(post.body || []).map((b, i) => (
            <div key={i}>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-primary tracking-tight">{b.h}</h2>
              {(b.p || []).map((para, j) => (
                <p key={j} className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
                  {linkify(para, { skipHref: `/blog/${post.slug}`, key: `b${i}p${j}` })}
                </p>
              ))}
            </div>
          ))}

          {/* CS01 deadline calculator surfaces inside any CS01-tagged post */}
          {(post.tags || []).some((t) => /^(cs01|confirmation statement)$/i.test(t)) && (
            <CS01Calculator />
          )}

          <div className="pt-8 border-t border-border flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap gap-2">
              {(post.tags || []).map((t) => (
                <span key={t} className="px-3 py-1 rounded-full bg-secondary text-primary/80 text-xs font-semibold">#{t}</span>
              ))}
            </div>
            <Button variant="outline" className="border-primary/20 text-primary">
              <Share2 className="h-4 w-4 mr-1.5" /> Share
            </Button>
          </div>
        </div>
      </section>

      <RelatedArticles
        items={more}
        current={{ slug: post.slug, tags: post.tags, category: post.category }}
        basePath="/blog"
        title="Related reading"
        testId="blog-related"
      />
    </SiteLayout>
  );
}
